import type { ReactNode } from 'react';
import type { Cohort } from '../shared/q3';
import { COHORT_RULES, FORCE_RULES } from '../shared/q3';
import type { ForceStanding, Standing } from './standings';
import { cr, pc } from './format';

export const COH: Record<Cohort, { link: string; name: string; em: string; cls: string; crit: ReactNode }> = {
  'Rising Star': {
    link: 'rising-stars',
    name: 'Rising Stars',
    em: 'sk-em-rising',
    cls: 'rise',
    crit: (
      <>
        Target <b>₹{COHORT_RULES['Rising Star'].target} Cr</b> per RM · ₹
        {COHORT_RULES['Rising Star'].target - COHORT_RULES['Rising Star'].min} Cr core wealth +{' '}
        <b>₹{COHORT_RULES['Rising Star'].min} Cr</b> Mandatory
      </>
    ),
  },
  'Super Star': {
    link: 'super-stars',
    name: 'Super Stars',
    em: 'sk-em-super',
    cls: 'super',
    crit: (
      <>
        Target <b>₹{COHORT_RULES['Super Star'].target} Cr</b> per RM · ₹
        {COHORT_RULES['Super Star'].target - COHORT_RULES['Super Star'].min} Cr core wealth +{' '}
        <b>₹{COHORT_RULES['Super Star'].min} Cr</b> Mandatory
      </>
    ),
  },
  'Star Force': {
    link: 'star-force',
    name: 'Star Force',
    em: 'sk-em-force',
    cls: 'force',
    crit: (
      <>
        IC and Wealth Acquisition qualify at <b>{FORCE_RULES.qualifyAt * 100}% achieved</b>, as reported in the MIS
      </>
    ),
  },
};

export function StatusTag({ r }: { r: Pick<Standing, 'qualified' | 'status'> }) {
  const cls = r.qualified ? 'q' : r.status === 'Mandatory gap' ? 'c' : 'r';
  return <span className={'sk-tag ' + cls}>{r.status}</span>;
}

/** "Almost there" card: who is closest to the line and what's left. */
export function NearCard({ r, showCohort }: { r: Standing; showCohort: boolean }) {
  return (
    <div className="sk-sc">
      <div className="nm">{r.rm}</div>
      {showCohort && <div className="sk-note">{COH[r.cohort].name + ' · ' + pc(r.pct) + ' of target'}</div>}
      <div className="g">{cr(r.gapCr) + (r.status === 'Mandatory gap' ? ' of Mandatory to go' : ' to qualify')}</div>
      <div className="tr">
        <div className="fl" style={{ width: Math.min(r.pct, 1) * 100 + '%' }} />
      </div>
    </div>
  );
}

/** Closest unqualified people who have some business on the board. */
export function nearest(rows: Standing[], n = 8): Standing[] {
  return rows.filter((r) => !r.qualified && r.ranked).sort((a, b) => b.pct - a.pct).slice(0, n);
}

export function nearestForce(rows: ForceStanding[], n = 8): ForceStanding[] {
  return rows.filter((r) => !r.qualified && r.ranked).sort((a, b) => b.pct - a.pct).slice(0, n);
}

/** Empty "Almost there": either nobody has started yet, or everyone's through. */
export function nearEmptyNote(rows: Array<{ qualified: boolean }>, everyone: string): string {
  return rows.some((r) => !r.qualified) ? 'No one has posted business yet.' : everyone;
}

export function ForceNearCard({ r }: { r: ForceStanding }) {
  return (
    <div className="sk-sc">
      <div className="nm">{r.rm}</div>
      <div className="sk-note">{r.role + ' · ' + r.progressLabel}</div>
      <div className="g">{r.needLabel}</div>
      <div className="tr">
        <div className="fl" style={{ width: Math.min(r.pct, 1) * 100 + '%' }} />
      </div>
    </div>
  );
}

/** Short pill label: the framework calls this role "Acquisition RM". */
export const RoleTag = ({ role }: { role: ForceStanding['role'] }) => (
  <span className={'sk-role ' + (role === 'IC' ? 'ic' : 'acq')} title={role}>
    {role === 'IC' ? 'IC' : 'Acquisition'}
  </span>
);
